import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import { ToastContainer } from "react-bootstrap";
import CustomToast from "../components/custom-toast/CustomToast";

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};

let globalToast = {
  success: (message) => console.warn("Toast not initialized", message),
  error: (message) => console.warn("Toast not initialized", message),
  warning: (message) => console.warn("Toast not initialized", message),
  info: (message) => console.warn("Toast not initialized", message),
  showToast: (message, variant) => console.warn("Toast not initialized", message, variant),
};

export const toast = {
  success: (message) => globalToast.success(message),
  error: (message) => globalToast.error(message),
  warning: (message) => globalToast.warning(message),
  info: (message) => globalToast.info(message),
  showToast: (message, variant) => globalToast.showToast(message, variant),
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, variant = "info") => {
    const id = Date.now(); // simple unique id
    const mappedVariant = variant === "error" ? "danger" : variant;
    setToasts((prev) => [
      ...prev,
      { id, message, variant: mappedVariant },
    ]);
  }, []);

  const hideToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback((message) => showToast(message, "success"), [showToast]);
  const error = useCallback((message) => showToast(message, "danger"), [showToast]);
  const warning = useCallback((message) => showToast(message, "warning"), [showToast]);
  const info = useCallback((message) => showToast(message, "info"), [showToast]);

  useEffect(() => {
    globalToast = {
      success,
      error,
      warning,
      info,
      showToast,
    };
  }, [success, error, warning, info, showToast]);

  const contextValue = useMemo(() => ({
    showToast,
    success,
    error,
    warning,
    info
  }), [showToast, success, error, warning, info]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}

      <ToastContainer
        position="top-end"
        className="p-3"
        style={{ position: 'fixed', top: '16px', right: '16px', zIndex: 9999 }}
      >
        {toasts.map((toast) => (
          <CustomToast
            key={toast.id}
            toast={toast}
            onClose={() => hideToast(toast.id)}
          />
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};

